import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ListPagination } from "@/components/list-pagination";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import type { EntryFilters as Filters } from "@/features/billing/api";
import { EntryFilters } from "@/features/billing/entry-filters";
import { readEntriesSearch } from "@/features/billing/search";
import { ResellerStatusBadge } from "@/features/reseller/status-badge";
import { ResellerDecisionActions } from "@/features/staff/decision-actions";
import { WALLET_ENTRIES_PER_PAGE } from "@/features/staff/api";
import { resellerQuery, resellerWalletEntriesQuery, resellerWalletQuery } from "@/features/staff/queries";
import { ResellerDetails } from "@/features/staff/reseller-details";
import { ResellerWallet } from "@/features/staff/reseller-wallet";
import { mayChangeStanding, mayDecideApplications } from "@/features/staff/roles";
import { ResellerStandingActions } from "@/features/staff/standing-actions";
import { ApiError } from "@/lib/api-client";
import { m } from "@/lib/paraglide/messages.js";

// /staff/resellers/:resellerId: one reseller, whatever its status, the decision on a pending
// application, suspending or reinstating it, and its wallet. The wallet's history is narrowed the
// way the reseller's own billing page is, by ?kind=, ?from=, ?to= (this month unless the URL
// names a range) and ?page= (guide, rule 16). The one file that reads the param (guide, rule 11).
// The API's 404 (no such reseller) and 422 (not an id) are this route's not-found page; the crumb
// is the company's name.
export const Route = createFileRoute("/staff/_authenticated/resellers/$resellerId")({
  validateSearch: (raw: Record<string, unknown>) => readEntriesSearch(raw),
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    filters: { kind: search.kind, from: search.from, to: search.to } satisfies Filters,
  }),
  loader: async ({ context, params, deps }) => {
    try {
      const reseller = await context.queryClient.ensureQueryData(resellerQuery(params.resellerId));
      await Promise.all([
        context.queryClient.ensureQueryData(resellerWalletQuery(params.resellerId)),
        context.queryClient.ensureQueryData(resellerWalletEntriesQuery(params.resellerId, deps.page, deps.filters)),
      ]);
      return { crumb: reseller.name };
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 422)) throw notFound();
      throw error;
    }
  },
  component: ResellerPage,
  notFoundComponent: ResellerNotFound,
});

function ResellerPage() {
  const { resellerId } = Route.useParams();
  const { staffMember } = Route.useRouteContext();
  const { page: asked = 1, kind, from, to } = Route.useSearch();
  const navigate = Route.useNavigate();
  const filters: Filters = { kind, from, to };
  const { data: reseller } = useSuspenseQuery(resellerQuery(resellerId));
  const { data: wallet } = useSuspenseQuery(resellerWalletQuery(resellerId));
  const { data: entries } = useSuspenseQuery(resellerWalletEntriesQuery(resellerId, asked, filters));
  const { page, total } = entries.meta;
  const pages = Math.max(1, Math.ceil(total / WALLET_ENTRIES_PER_PAGE));
  const shown = entries.data.length;
  const first = shown === 0 ? 0 : (page - 1) * WALLET_ENTRIES_PER_PAGE + 1;
  const range = m.common_page_range({ from: first, to: first + shown - (shown ? 1 : 0), total });
  // As on the reseller's billing page: a changed filter starts again from the first page, replaced
  // rather than pushed so Back leaves the page.
  const onFiltersChange = (next: Filters) =>
    navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true });
  return (
    <>
      <Item>
        <ItemContent>
          <ItemTitle>{reseller.name}</ItemTitle>
          <ItemDescription>
            <ResellerStatusBadge status={reseller.status} />
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <ResellerDecisionActions reseller={reseller} canDecide={mayDecideApplications(staffMember.role)} />
          <ResellerStandingActions reseller={reseller} canChange={mayChangeStanding(staffMember.role)} />
        </ItemActions>
      </Item>
      <ResellerDetails reseller={reseller} />
      <ResellerWallet
        balance={wallet.balance}
        entries={entries.data}
        caption={range}
        filters={<EntryFilters filters={filters} onChange={onFiltersChange} />}
        pagination={<ListPagination page={page} pages={pages} />}
        footer={
          <Button variant="link" render={<Link to="/staff/top-ups" search={{ reseller: reseller.id }} />}>
            {m.staff_reseller_wallet_top_ups()}
          </Button>
        }
      />
    </>
  );
}

function ResellerNotFound() {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>{m.staff_reseller_not_found_title()}</EmptyTitle>
        <EmptyDescription>{m.staff_reseller_not_found_description()}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button variant="outline" render={<Link to="/staff/resellers" />}>
          {m.staff_reseller_back()}
        </Button>
      </EmptyContent>
    </Empty>
  );
}
